// components/BluetoothConnect.tsx
import React, { useEffect, useRef, useState } from 'react';
import './BluetoothConnect.css';

// ---- 최소 타입 선언: TS에서 Web Bluetooth 타입이 없을 때 에러 방지 ----
declare global {
  interface Navigator {
    bluetooth?: any;
  }
}
type BluetoothDevice = any;
type BluetoothRemoteGATTServer = any;
type BluetoothRemoteGATTCharacteristic = any;
// -------------------------------------------------------------------------

const SERVICE_UUID = '4fafc201-1fb5-459e-8fcc-c5c9c331914b';
const CHARACTERISTIC_UUID = 'beb5483e-36e1-4688-b7f5-ea07361b26a8';

const BluetoothConnect: React.FC = () => {
  const [supported, setSupported] = useState<boolean>(false);
  const [device, setDevice] = useState<BluetoothDevice | null>(null);
  const [server, setServer] = useState<BluetoothRemoteGATTServer | null>(null);
  const [characteristic, setCharacteristic] = useState<BluetoothRemoteGATTCharacteristic | null>(null);
  const [status, setStatus] = useState<string>('대기 중');
  const [isSent, setIsSent] = useState<boolean>(false);

  // Wi-Fi/MQTT 전송값
  const [ssid, setSsid] = useState<string>(() => localStorage.getItem('bt_ssid') || '');
  const [pw, setPw] = useState<string>(() => localStorage.getItem('bt_pw') || '');
  const [email, setEmail] = useState<string>(() => localStorage.getItem('bt_email') || '');
  const [broker, setBroker] = useState<string>(() => localStorage.getItem('bt_broker') || 'mqtt.i2r.link');

  useEffect(() => {
    localStorage.setItem('bt_ssid', ssid);
    localStorage.setItem('bt_pw', pw);
    localStorage.setItem('bt_email', email);
    localStorage.setItem('bt_broker', broker);
  }, [ssid, pw, email, broker]);

  useEffect(() => {
    setSupported(!!navigator.bluetooth);
  }, []);

  const [isSending, setIsSending] = useState<boolean>(false);

  // 연결 방식: 블루투스 / COM 포트(USB, Web Serial)
  // 기본은 COM 포트(USB). 사용자가 블루투스를 직접 골랐을 때만 블루투스로 시작한다.
  // (예전 저장값 'bt_method' 는 기본값 'ble' 이 자동 저장된 것이라 쓰지 않고 새 이름을 쓴다)
  const [method, setMethod] = useState<'ble' | 'serial'>(() => {
    try {
      return localStorage.getItem('bt_method_v2') === 'ble' ? 'ble' : 'serial';
    } catch (e) {
      return 'serial';
    }
  });
  const serialSupported = typeof navigator !== 'undefined' && !!(navigator as any).serial;

  useEffect(() => {
    try {
      localStorage.setItem('bt_method_v2', method);
    } catch (e) {
      // 저장소를 못 쓰면 건너뛴다
    }
  }, [method]);

  const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

  // COM 포트로 설정 전송: (선택) ak -> si, 응답 JSON 한 줄씩 확인
  const sendViaSerial = async () => {
    const serial = (navigator as any).serial;
    if (!serial) { setStatus('이 브라우저는 Web Serial을 지원하지 않습니다'); return; }
    if (!ssid.trim()) { setStatus('SSID를 입력하세요'); return; }
    setIsSent(false);
    let port: any = null;
    let reader: any = null;
    let writer: any = null;
    let alive = true;
    const lines: string[] = [];
    try {
      setStatus('COM 포트 선택 중…');
      port = await serial.requestPort();
      await port.open({ baudRate: 115200 });
      try { await port.setSignals({ dataTerminalReady: false, requestToSend: false }); } catch (e) { /* 무시 */ }
      setIsSending(true);

      // 수신 루프 (한 줄씩 모음)
      reader = port.readable.getReader();
      const dec = new TextDecoder();
      let buf = '';
      const readLoop = (async () => {
        while (alive) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += dec.decode(value);
          let i;
          while ((i = buf.indexOf('\n')) >= 0) {
            lines.push(buf.slice(0, i).trim());
            buf = buf.slice(i + 1);
          }
        }
      })().catch(() => { /* 포트 닫힘 */ });

      const waitFor = async (c: string, ms: number) => {
        const t0 = Date.now();
        while (Date.now() - t0 < ms) {
          for (const l of lines) {
            if (l[0] !== '{') continue;
            try {
              const j = JSON.parse(l);
              if (j.c === c) return j;
            } catch (e) { /* 일반 로그 줄 */ }
          }
          await sleep(100);
        }
        return null;
      };

      setStatus('보드 준비 대기 중… (약 3초)');
      await sleep(3000);

      writer = port.writable.getWriter();
      const enc = new TextEncoder();
      const send = async (obj: any) => { await writer.write(enc.encode(JSON.stringify(obj) + '\n')); };

      setStatus('설정 전송 중…');
      lines.length = 0;
      await send({
        c: 'si',
        ssid: ssid.trim(),
        password: pw.trim(),
        e: email.trim(),
        mqttBroker: broker.trim() || 'i2r',
      });
      const r = await waitFor('si', 5000);
      if (!r || !r.ok) throw new Error('설정 응답 없음 (PROG 포트인지, 시리얼 모니터가 닫혀 있는지 확인)');

      setIsSent(true);
      setStatus('전송 완료 (보드가 재시작됩니다)');
      alive = false;
      void readLoop;
    } catch (err: any) {
      setStatus(err && err.name === 'NotFoundError' ? '취소됨' : `에러: ${err?.message || err}`);
    } finally {
      alive = false;
      try { writer?.releaseLock(); } catch (e) { /* */ }
      try { await reader?.cancel(); } catch (e) { /* */ }
      try { reader?.releaseLock(); } catch (e) { /* */ }
      try { await port?.close(); } catch (e) { /* */ }
      setIsSending(false);
    }
  };

  const connect = async () => {
    setIsSent(false);
    if (!navigator.bluetooth) {
      setSupported(false);
      return;
    }
    try {
      setStatus('기기 검색 중…');
      const dev: BluetoothDevice = await navigator.bluetooth.requestDevice({
        filters: [{ namePrefix: 'i2r' }],
        optionalServices: [SERVICE_UUID],
      });
      setDevice(dev);

      dev.addEventListener?.('gattserverdisconnected', handleDisconnected);

      setStatus('기기 연결 중…');
      const gatt: BluetoothRemoteGATTServer | undefined = await dev.gatt?.connect();
      if (!gatt) throw new Error('GATT 연결 실패');
      setServer(gatt);

      const service = await gatt.getPrimaryService(SERVICE_UUID);
      const ch = await service.getCharacteristic(CHARACTERISTIC_UUID);
      setCharacteristic(ch);

      // 연결 성공 즉시 Wi-Fi / MQTT 설정 전송
      setStatus('설정 전송 중...');
      setIsSending(true);
      const payload = {
        c: 'si',
        ssid: ssid.trim(),
        password: pw.trim(),
        e: email.trim(),
        mqttBroker: broker.trim() || 'i2r',
      };

      const enc = new TextEncoder().encode(JSON.stringify(payload));
      await ch.writeValue(enc);
      setIsSent(true);
      setStatus('전송 완료 (연결 완료)');

    } catch (err) {
      setStatus('에러 또는 취소됨');
    } finally {
      setIsSending(false);
    }
  };

  const handleDisconnected = () => {
    setStatus('대기 중');
    setServer(null);
    setCharacteristic(null);
    setIsSent(false);
    setDevice(null);
  };

  return (
    <div className="bt-wrap">
      <h2 className="bt-title">기기연결 ({method === 'serial' ? 'COM 포트' : 'Bluetooth'})</h2>

      <div className="bt-row">
        <label style={{ marginRight: 16 }}>
          <input type="radio" name="bt_method" checked={method === 'serial'} onChange={() => setMethod('serial')} /> COM 포트 (USB)
        </label>
        <label>
          <input type="radio" name="bt_method" checked={method === 'ble'} onChange={() => setMethod('ble')} /> 블루투스
        </label>
      </div>

      {method === 'ble' ? (
        <p className="bt-info">
          블루투스 모듈이 있어야 하며, 크롬 브라우저를 사용하세요.<br />
          안드로이드와 맥북은 동작하지만 아이폰은 연결되지 않습니다.
        </p>
      ) : (
        <p className="bt-info">
          PC 크롬/엣지에서 USB 케이블로 연결하세요. (보드의 PROG 포트, 시리얼 모니터는 닫기)<br />
          전송 후 보드가 재시작됩니다.
        </p>
      )}

      {method === 'ble' && !supported && (
        <p className="bt-warn">
          이 브라우저는 Web Bluetooth를 지원하지 않습니다. (HTTPS, Chrome/Edge/Android 권장)
        </p>
      )}
      {method === 'serial' && !serialSupported && (
        <p className="bt-warn">
          이 브라우저는 Web Serial을 지원하지 않습니다. (PC의 Chrome/Edge, HTTPS 필요)
        </p>
      )}

      <div className="bt-status"><b>상태:</b> {status}</div>

      <div className="bt-row">
        {method === 'ble' ? (
          <button className="bt-btn" onClick={connect} disabled={!supported || isSending}>
            {isSending ? '처리 중...' : 'i2r* 기기 검색 / 자동 연결'}
          </button>
        ) : (
          <button className="bt-btn" onClick={sendViaSerial} disabled={!serialSupported || isSending}>
            {isSending ? '처리 중...' : 'COM 포트 선택 / 설정 전송'}
          </button>
        )}
      </div>

      <fieldset className="bt-fieldset">
        <legend className="bt-legend">Wi-Fi / MQTT 설정 전송</legend>

        <div className="bt-grid">
          <label>SSID</label>
          <input className="bt-input" name="iot_ssid" type="text" value={ssid} onChange={(e) => setSsid(e.target.value)} placeholder="SSID" autoComplete="off" />

          <label>PW</label>
          <input className="bt-input" name="iot_pw" type="text" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Password" autoComplete="off" />

          <label>Email</label>
          <input className="bt-input" name="iot_email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" autoComplete="email" />

          <label>Broker</label>
          <input className="bt-input" name="iot_broker" type="text" value={broker} onChange={(e) => setBroker(e.target.value)} placeholder="mqtt.i2r.link" autoComplete="nope" />

        </div>

      </fieldset>
    </div>
  );
};

export default BluetoothConnect;
